import React from "react";
import { CellContext } from "@tanstack/react-table";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { HStack } from "@design-system/HStack/HStack";
import { StateAwareLink } from "@components/routes/StateAwareLink";
import { Tag } from "@design-system/Tag/Tag";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import { useCopyToClipboard } from "@hooks/useCopyToClipboard";
import classNames from "classnames";
import {
    TokenSample as TokenSampleType,
    tokenCopyButtonId,
    TokenSourceReference,
} from "./designTokensCatalog";
import { MotionTokenSample } from "./designTokensMotionCells";
import { TokenViewRow } from "./designTokensViewRows";
import { designSystemEntryPath } from "./playgroundComponents";
import styles from "./DesignTokensPlayground.module.scss";

const resolveTokenValue = (tokenName: `--${string}`) => {
    const value = getComputedStyle(document.documentElement)
        .getPropertyValue(tokenName)
        .trim();

    return value.length > 0 ? value : null;
};

interface TokenSampleProps {
    sample: TokenSampleType;
    tokenName: `--${string}`;
    value: string | null;
}

const TokenSample = ({ sample, tokenName, value }: TokenSampleProps) => {
    if (value === null) {
        return <span aria-hidden="true" className={styles.sample} />;
    }

    switch (sample) {
        case "color":
            return (
                <span
                    aria-hidden="true"
                    className={classNames(styles.sample, styles.colorSample)}
                    style={{ backgroundColor: `var(${tokenName})` }}
                />
            );
        case "opacity":
            return (
                <span
                    aria-hidden="true"
                    className={classNames(styles.sample, styles.opacitySample)}
                    style={{ opacity: `var(${tokenName})` }}
                >
                    The quick brown fox
                </span>
            );
        case "spacing":
            return (
                <span
                    aria-hidden="true"
                    className={classNames(styles.sample, styles.spacingSample)}
                    style={{ width: value }}
                />
            );
        case "border":
            return (
                <span
                    aria-hidden="true"
                    className={classNames(styles.sample, styles.borderSample)}
                    style={{
                        borderWidth: value,
                        borderStyle: "solid",
                        borderColor: "var(--border-default)",
                    }}
                />
            );
        case "radius":
            return (
                <span
                    aria-hidden="true"
                    className={classNames(styles.sample, styles.radiusSample)}
                    style={{
                        borderRadius: value,
                        backgroundColor: "var(--bg-accent-selected)",
                    }}
                />
            );
        case "shadow":
            return (
                <span
                    aria-hidden="true"
                    className={classNames(styles.sample, styles.shadowSample)}
                    style={{ boxShadow: value }}
                />
            );
        case "fontFamily":
            return (
                <span
                    aria-hidden="true"
                    className={classNames(styles.sample, styles.fontSample)}
                    style={{ fontFamily: value }}
                >
                    The quick brown fox
                </span>
            );
        case "fontSize":
            return (
                <span
                    aria-hidden="true"
                    className={classNames(styles.sample, styles.fontSample)}
                    style={{ fontSize: value }}
                >
                    The quick brown fox
                </span>
            );
        case "lineHeight":
            return (
                <span
                    aria-hidden="true"
                    className={classNames(styles.sample, styles.fontSample)}
                    style={{ lineHeight: value }}
                >
                    The quick brown fox
                    <br />
                    jumps over the lazy dog
                </span>
            );
        case "fontWeight":
            return (
                <span
                    aria-hidden="true"
                    className={classNames(styles.sample, styles.fontSample)}
                    style={{ fontWeight: value }}
                >
                    The quick brown fox
                </span>
            );
        case "tracking":
            return (
                <span
                    aria-hidden="true"
                    className={classNames(styles.sample, styles.trackingSample)}
                    style={{ letterSpacing: value }}
                >
                    The quick brown fox
                </span>
            );
        case "easing":
        case "duration":
            return <MotionTokenSample sample={sample} tokenName={tokenName} />;
    }
};

const SourceTokenLink = ({ source }: { source: TokenSourceReference }) => (
    <StateAwareLink
        aria-label={`Show source token ${source.tokenName}`}
        className={styles.sourceLink}
        to={`${designSystemEntryPath(source.tabId)}#${tokenCopyButtonId(source.tokenName)}`}
    >
        <code className={styles.tokenName}>{source.tokenName}</code>
    </StateAwareLink>
);

export const DesignTokenNameCell = React.memo(function DesignTokenNameCell({
    row,
}: CellContext<ViewTableFeatures, TokenViewRow, unknown>): React.ReactElement {
    const copyToClipboard = useCopyToClipboard();
    const { label, token, mixin } = row.original;

    if (token !== undefined) {
        const copyValue = `var(${token.name})`;

        return (
            <ViewTableCell>
                <HStack gap={8} align="center">
                    <button
                        aria-label={`Copy ${copyValue}`}
                        className={styles.copyButton}
                        id={tokenCopyButtonId(token.name)}
                        onClick={() => copyToClipboard(copyValue)}
                        type="button"
                    >
                        <code className={styles.tokenName}>{token.name}</code>
                    </button>
                    {token.isDeviation && (
                        <Tag size="sm" color="neutral">
                            Deviation
                        </Tag>
                    )}
                </HStack>
            </ViewTableCell>
        );
    }

    if (mixin !== undefined) {
        return (
            <ViewTableCell>
                <button
                    aria-label={`Copy ${mixin.copyValue}`}
                    className={classNames(styles.copyButton, styles.mixinName)}
                    onClick={() => copyToClipboard(mixin.copyValue)}
                    type="button"
                >
                    <code className={styles.tokenName}>{mixin.name}</code>
                </button>
            </ViewTableCell>
        );
    }

    return <ViewTableCell className={styles.groupLabel}>{label}</ViewTableCell>;
});

export const DesignTokenOriginCell = React.memo(function DesignTokenOriginCell({
    row,
}: CellContext<ViewTableFeatures, TokenViewRow, unknown>): React.ReactElement {
    const { token, mixin } = row.original;
    const entry = token ?? mixin;

    if (entry === undefined) {
        return <ViewTableCell />;
    }

    return (
        <ViewTableCell>
            {entry.isNotInFigma ? (
                <Tag size="sm" color="neutral">
                    Codebase
                </Tag>
            ) : (
                <Tag size="sm" color="neutral">
                    Figma
                </Tag>
            )}
        </ViewTableCell>
    );
});

export const DesignTokenSourceCell = React.memo(function DesignTokenSourceCell({
    row,
}: CellContext<ViewTableFeatures, TokenViewRow, unknown>): React.ReactElement {
    const { token, mixin } = row.original;

    if (token?.sourceToken !== undefined) {
        return (
            <ViewTableCell>
                <SourceTokenLink
                    source={{
                        tabId: "primitive-colors",
                        tokenName: token.sourceToken,
                    }}
                />
            </ViewTableCell>
        );
    }

    if (mixin !== undefined) {
        return (
            <ViewTableCell>
                <HStack
                    wrap
                    gap={4}
                    align="center"
                    className={styles.mixinSource}
                >
                    {mixin.sourceTokens.map((sourceToken) => (
                        <SourceTokenLink
                            key={sourceToken}
                            source={{ tabId: "font", tokenName: sourceToken }}
                        />
                    ))}
                    {mixin.recipeDetail !== undefined && (
                        <code className={styles.recipeDetail}>
                            {mixin.recipeDetail}
                        </code>
                    )}
                </HStack>
            </ViewTableCell>
        );
    }

    return <ViewTableCell />;
});

export const DesignTokenValueCell = React.memo(function DesignTokenValueCell({
    row,
}: CellContext<ViewTableFeatures, TokenViewRow, unknown>): React.ReactElement {
    const { token, mixin } = row.original;

    if (token !== undefined) {
        return (
            <ViewTableCell>
                <code className={styles.resolvedValue}>
                    {resolveTokenValue(token.name) ?? "Not available"}
                </code>
            </ViewTableCell>
        );
    }

    if (mixin !== undefined) {
        return (
            <ViewTableCell>
                <code
                    className={classNames(
                        styles.resolvedValue,
                        styles.mixinValue,
                    )}
                >
                    {mixin.copyValue}
                </code>
            </ViewTableCell>
        );
    }

    return <ViewTableCell />;
});

export const DesignTokenSampleCell = React.memo(function DesignTokenSampleCell({
    row,
}: CellContext<ViewTableFeatures, TokenViewRow, unknown>): React.ReactElement {
    const { token, mixin } = row.original;

    if (token !== undefined) {
        return (
            <ViewTableCell>
                <TokenSample
                    sample={token.sample}
                    tokenName={token.name}
                    value={resolveTokenValue(token.name)}
                />
            </ViewTableCell>
        );
    }

    if (mixin !== undefined) {
        return (
            <ViewTableCell>
                <span
                    aria-hidden="true"
                    className={classNames(
                        styles[mixin.sampleClass],
                        styles.mixinSample,
                    )}
                >
                    The quick brown fox
                </span>
            </ViewTableCell>
        );
    }

    return <ViewTableCell />;
});
